import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const WORDS = [
  'confession', 'anonymous', 'whisper', 'secret', 'truthful', 'midnight', 'silence', 'courage',
  'shadow', 'stranger', 'forgive', 'promise', 'reveal', 'diary', 'honest', 'regret'
];

const scramble = (word) => {
  const letters = word.split('');
  for (let i = letters.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [letters[i], letters[j]] = [letters[j], letters[i]];
  }
  const out = letters.join('');
  return out === word ? scramble(word) : out;
};

export function WordScramble({ onScore }) {
  const [word, setWord] = useState('');
  const [shuffled, setShuffled] = useState('');
  const [guess, setGuess] = useState('');
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [running, setRunning] = useState(false);
  const [feedback, setFeedback] = useState('');

  const nextWord = useCallback(() => {
    const picked = WORDS[Math.floor(Math.random() * WORDS.length)];
    setWord(picked);
    setShuffled(scramble(picked));
    setGuess('');
  }, []);

  useEffect(() => { nextWord(); }, [nextWord]);

  useEffect(() => {
    if (!running) return;
    if (timeLeft <= 0) {
      setRunning(false);
      setFeedback(`Time up! You solved ${score} words.`);
      onScore(score);
      return;
    }
    const t = setTimeout(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, timeLeft]);

  const start = () => {
    setScore(0);
    setTimeLeft(60);
    setFeedback('');
    setRunning(true);
    nextWord();
  };

  const check = () => {
    if (!running) return;
    if (guess.trim().toLowerCase() === word) {
      setScore((s) => s + 1);
      setFeedback('Correct!');
      nextWord();
    } else {
      setFeedback('Not quite — try again');
      setGuess('');
    }
  };

  return (
    <div className="space-y-4" data-testid="game-word-scramble">
      <div className="flex items-center justify-between text-sm">
        <span>Solved: <span className="text-amber-300 tabular-nums" data-testid="scramble-score">{score}</span></span>
        <span className="text-muted-foreground tabular-nums" data-testid="scramble-timer">{timeLeft}s</span>
      </div>

      <div className="py-8 text-center border border-white/10 rounded-lg bg-white/5">
        <p className="text-3xl tracking-[0.4em] uppercase" data-testid="scramble-word">{shuffled}</p>
        <p className="text-xs text-muted-foreground mt-2">{word.length} letters</p>
      </div>

      <div className="flex gap-2">
        <Input
          placeholder="Unscramble it"
          value={guess}
          onChange={(e) => setGuess(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && check()}
          disabled={!running}
          data-testid="scramble-input"
        />
        <Button onClick={check} disabled={!running} className="rounded-full" data-testid="scramble-submit">Guess</Button>
      </div>

      {feedback && <p className="text-sm text-center text-muted-foreground" data-testid="scramble-feedback">{feedback}</p>}

      <Button variant="outline" className="rounded-full w-full" onClick={start} data-testid="scramble-start">
        {running ? 'Restart 60s round' : 'Start 60s round'}
      </Button>
    </div>
  );
}
